import * as Sentry from '@sentry/react';

const SENSITIVE_KEYS = /token|password|secret|authorization|cookie|session|credit.?card|ssn|api.?key|preview|transcript|user.?input|assistant.?response|coaching|health.?data/i;

const MAX_SAFE_VALUE_LENGTH = 500;

function scrubData(data, depth = 0) {
  if (!data || typeof data !== 'object' || depth > 5) return data;
  const scrubbed = Array.isArray(data) ? [...data] : { ...data };
  for (const key of Object.keys(scrubbed)) {
    if (SENSITIVE_KEYS.test(key)) {
      scrubbed[key] = '[Filtered]';
    } else if (typeof scrubbed[key] === 'string' && scrubbed[key].length > MAX_SAFE_VALUE_LENGTH) {
      scrubbed[key] = `[Truncated: ${scrubbed[key].length} chars]`;
    } else if (typeof scrubbed[key] === 'object' && scrubbed[key] !== null) {
      scrubbed[key] = scrubData(scrubbed[key], depth + 1);
    }
  }
  return scrubbed;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.debug('[Sentry] No DSN configured — skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.DEV
      ? 'development'
      : (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production'),
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    sendDefaultPii: false,
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      if (event.request?.headers) {
        event.request.headers = scrubData(event.request.headers);
      }
      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrubData(event.request.data);
      }
      if (event.extra) {
        event.extra = scrubData(event.extra);
      }
      if (event.contexts) {
        event.contexts = scrubData(event.contexts);
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data && typeof breadcrumb.data === 'object') {
        breadcrumb.data = scrubData(breadcrumb.data);
      }
      if (breadcrumb.message && breadcrumb.message.length > MAX_SAFE_VALUE_LENGTH) {
        breadcrumb.message = `[Truncated: ${breadcrumb.message.length} chars]`;
      }
      return breadcrumb;
    },
  });
}

export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;

export function setUser(user) {
  Sentry.setUser(user);
}

export function setTag(key, value) {
  Sentry.setTag(key, value);
}
